import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu,
  X,
  Home,
  Info,
  FileText,
  Star,
} from 'lucide-react';
import { default as logo } from '../assets/logo.png';
import { default as sacredLogo } from '../assets/sacred.png';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { lang } = useParams();
  const { t, i18n } = useTranslation('common');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // Get current language from URL or i18n
  const currentLang = lang || i18n.language || 'en';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { label: t('header.menu.home'), path: `/${currentLang}`, icon: Home },
    { label: t('header.menu.about'), path: `/${currentLang}/about`, icon: Info },
    { label: t('header.menu.awards'), path: `/${currentLang}/nominations`, icon: FileText },
    // { label: 'Directory', path: `/${currentLang}/directory`, icon: FolderOpen },
    { label: t('header.menu.winners'), path: `/${currentLang}/winners`, icon: Star },
    // { label: 'Gallery', path: `/${currentLang}/gallery`, icon: Camera },
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavigation = () => {
    // Scroll to top when navigating to a new page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Check if we're on the home page (language-aware routing)
  const isHomePage = location.pathname === '/en' || location.pathname === '/kn' || location.pathname === '/';
  const isTransparent = isHomePage && !isScrolled;

  return (
    <AppBar
      elevation={isTransparent ? 0 : 1}
      sx={{
        backgroundColor: isTransparent ? 'transparent' : '#FFFFFF',
        overflow: 'visible',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{
          justifyContent: 'space-between',
          minHeight: '80px !important',
          backgroundColor: isTransparent ? 'transparent' : '#FFFFFF',
          overflow: 'visible',
        }}>
          {/* Logos */}
          <Box component={Link} to={`/${currentLang}`} onClick={handleNavigation} sx={{ display: 'flex', alignItems: 'center', gap: 2, textDecoration: 'none' }}>
            <Box
              component="img"
              src={sacredLogo}
              alt="Sacred Logo"
              sx={{
                height: { xs: 40, md: 48 },
                width: 'auto',
                mr: 2,
                transition: 'filter 0.3s ease'
              }}
            />
            <Box
              component="img"
              src={logo}
              alt="Bhoomee Awards"
              sx={{ height: 48, width: 'auto' }}
            />
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'visible' }}>
              <List sx={{ display: 'flex', alignItems: 'center', p:0, overflow: 'visible' }}>
                {menuItems.map((item) => (
                  <ListItem
                    key={item.path}
                    component={Link}
                    to={item.path}
                    onClick={handleNavigation}
                    sx={{
                      color: isActive(item.path)
                        ? '#D4A747'
                        : isTransparent ? '#FFFFFF' : '#2B2B2B',
                      fontWeight: 500,
                      fontSize: '1rem',
                      fontFamily: 'Inter, sans-serif',
                      textTransform: 'none',
                      transition: 'color 0.3s ease',
                      mx: 1,
                      pl: 1.2,
                      pr: 1.2,
                      pt: 1,
                      pb: 1.5,
                      position: 'relative',
                      overflow: 'visible',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        width: isActive(item.path) ? '70%' : '0%',
                        height: '2px',
                        backgroundColor: '#D4A747',
                        transform: 'translateX(-50%)',
                        transition: 'width 0.3s ease-in-out',
                      },
                      '&:hover': {
                        color: '#D4A747',
                      },
                      '&:hover::after': {
                        width: '80%',
                      }
                    }}
                  >
                    {item.label}
                  </ListItem>
                ))}
              </List>

              {/* Language Switcher */}
              <LanguageSwitcher isTransparent={isTransparent} />
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              onClick={() => setIsOpen(!isOpen)}
              sx={{ color: isTransparent ? '#FFFFFF' : '#D4A747' }}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </IconButton>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="top"
        open={isOpen && isMobile}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            marginTop: '80px',
            overflowX: 'hidden',
            width: '100%',
          },
        }}
      >
        <Box sx={{ py: 2, width: '100%', overflowX: 'hidden' }}>
          <List sx={{ width: '100%', overflowX: 'hidden' }}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <ListItem
                  key={item.path}
                  component={Link}
                  to={item.path}
                  onClick={() => {
                    setIsOpen(false);
                    handleNavigation();
                  }}
                  sx={{
                    mx: 2,
                    mb: 1,
                    borderRadius: 2,
                    backgroundColor: isActive(item.path) ? 'primary.main' : 'transparent',
                    color: isActive(item.path) ? 'primary.contrastText' : 'text.primary',
                    '&:hover': {
                      backgroundColor: isActive(item.path) ? 'primary.main' : 'grey.100',
                    },
                    textDecoration: 'none',
                    width: 'auto',
                    maxWidth: '100%',
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                    <Icon size={20} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
              );
            })}
          </List>

          {/* Mobile Language Switcher */}
          <Box sx={{ px: 2, pb: 2 }}>
            <LanguageSwitcher isTransparent={false} />
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;